// Rank-based roster ordering. Police hierarchy (high → low):
//   Chief > Major > Captain > Lieutenant > Master Sergeant > Sergeant
//   > Corporal > Officer
//
// Employee.rank is free-text, so we normalize common abbreviations
// + spellings to an integer tier. Unknown / blank ranks sort last,
// in a stable alphabetical order.
//
// Tie-break rule (within a tier): ascending badge number. We
// attempt numeric parse first for natural order (#9 before #10);
// falls back to localeCompare for non-numeric badges.

const TIER_PATTERNS: Array<{ tier: number; test: (s: string) => boolean }> = [
  { tier: 0, test: (s) => /^chief\b/.test(s) },
  { tier: 1, test: (s) => /^(maj|major)\b/.test(s) },
  { tier: 2, test: (s) => /^(cpt|capt|captain)\b/.test(s) },
  { tier: 3, test: (s) => /^(lt|lieutenant)\b/.test(s) },
  { tier: 4, test: (s) => /^(msgt|master\s*sergeant|master\s*sgt)\b/.test(s) },
  { tier: 5, test: (s) => /^(sgt|sergeant)\b/.test(s) },
  { tier: 6, test: (s) => /^(cpl|corporal)\b/.test(s) },
  { tier: 7, test: (s) => /^(ofc|ofcr|officer|ptl|pfc)\b/.test(s) },
];

export function rankTier(rank: string | null | undefined): number {
  const s = (rank ?? "").trim().toLowerCase();
  if (!s) return 999;
  for (const { tier, test } of TIER_PATTERNS) {
    if (test(s)) return tier;
  }
  return 999;
}

export function compareByRankThenBadge(
  a: { rank: string | null; badgeNumber: string | null; lastName: string; firstName: string },
  b: { rank: string | null; badgeNumber: string | null; lastName: string; firstName: string },
): number {
  const ra = rankTier(a.rank);
  const rb = rankTier(b.rank);
  if (ra !== rb) return ra - rb;
  // Badge: numeric ascending when both parse, else lexicographic.
  const na = Number((a.badgeNumber ?? "").replace(/[^\d]/g, ""));
  const nb = Number((b.badgeNumber ?? "").replace(/[^\d]/g, ""));
  const aNum = !Number.isNaN(na) && (a.badgeNumber ?? "").length > 0;
  const bNum = !Number.isNaN(nb) && (b.badgeNumber ?? "").length > 0;
  if (aNum && bNum && na !== nb) return na - nb;
  if (aNum && !bNum) return -1;
  if (!aNum && bNum) return 1;
  if (aNum && bNum) return (a.badgeNumber ?? "").localeCompare(b.badgeNumber ?? "");
  // Final fallback: name.
  return (a.lastName || "").localeCompare(b.lastName || "")
    || (a.firstName || "").localeCompare(b.firstName || "");
}

export type SeniorityMode = "rank_badge" | "radio_rank_badge";

// Compare two roster entries using the active seniority mode.
// "radio_rank_badge" sorts by radio number first (numeric
// ascending when both parse, lexicographic otherwise), with
// rank+badge as the fallback when one or both officers don't
// have a radio number on file. That way an agency that issues
// every patrol officer a numbered portable can sort the schedule
// by call sign, while supervisors / civilians without a portable
// still slot into a sensible position via rank+badge.
export function compareBySeniority(
  mode: SeniorityMode,
  a: { rank: string | null; badgeNumber: string | null; radioNumber: string | null;
       lastName: string; firstName: string },
  b: { rank: string | null; badgeNumber: string | null; radioNumber: string | null;
       lastName: string; firstName: string },
): number {
  if (mode === "radio_rank_badge") {
    const ra = (a.radioNumber ?? "").trim();
    const rb = (b.radioNumber ?? "").trim();
    if (ra && rb) {
      const na = Number(ra.replace(/[^\d]/g, ""));
      const nb = Number(rb.replace(/[^\d]/g, ""));
      const aNum = !Number.isNaN(na) && /\d/.test(ra);
      const bNum = !Number.isNaN(nb) && /\d/.test(rb);
      if (aNum && bNum && na !== nb) return na - nb;
      const lex = ra.localeCompare(rb, undefined, { numeric: true, sensitivity: "base" });
      if (lex !== 0) return lex;
    } else if (ra && !rb) {
      // Officer with a radio sorts above an officer without — the
      // person who'd be addressed by call sign on the air goes first.
      return -1;
    } else if (!ra && rb) {
      return 1;
    }
    // Both lack a radio (or tied on call sign) — fall through to
    // rank+badge so unit-less officers still order sensibly.
  }
  return compareByRankThenBadge(a, b);
}
